import React, {createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Canvas, useThree} from '@react-three/fiber';
import {Line, OrthographicCamera, Text} from '@react-three/drei';
import {Shape, Vector3} from 'three';
import {
    buildCuttingMapScene,
    getSceneBounds,
    CUT_LINE_WIDTH,
    DETAIL_OPACITY,
    EDGE_LINE_WIDTH,
    HATCH_LINE_WIDTH,
    HATCH_OPACITY,
    OUTLINE_LINE_WIDTH,
    SHEET_INNER_COLOR,
    SHEET_OUTER_LINE_WIDTH,
    DETAIL_OUTLINE_COLOR,
    CUTOFF_OUTLINE_COLOR,
    CUTOFF_OUTLINE_INSET,
} from '../../../utils/cutting/drawCuttingMap';

/** Cut / edge highlight (details keep their turn color on hover) */
const HOVER_COLOR = '#3B8BEC';
const RULER_CROSS_COLOR = '#737478';
const RULER_LABEL_COLOR = '#1A1A1A';
const RULER_LABEL_OUTLINE_COLOR = '#ffffff';
const MIN_ZOOM_MULTIPLIER = 1;
const MAX_ZOOM_MULTIPLIER = 5;
const ZOOM_STEP = 1.15;

/** Solid crosshair segment via plane mesh — more reliable than Line2 over fills. */
const RulerSegment = ({from, to, color, z = 60, screenThickness = 3, renderOrder = 1000}) => {
    const {camera} = useThree();
    const x0 = from[0];
    const y0 = from[1];
    const x1 = to[0];
    const y1 = to[1];
    const len = Math.hypot(x1 - x0, y1 - y0);
    if (len < 0.5) {
        return null;
    }
    const thickness = screenThickness / Math.max(camera.zoom, 0.001);
    const horizontal = Math.abs(y1 - y0) <= Math.abs(x1 - x0);
    return (
        <mesh
            position={[(x0 + x1) / 2, (y0 + y1) / 2, z]}
            renderOrder={renderOrder}
            raycast={noRaycast}
        >
            <planeGeometry args={horizontal ? [len, thickness] : [thickness, len]}/>
            <meshBasicMaterial
                color={color}
                toneMapped={false}
                transparent
                opacity={1}
                depthTest={false}
                depthWrite={false}
            />
        </mesh>
    );
};

const getSheetOrigin = (sheet) => {
    const trimming = sheet?.trimming ?? [0, 0, 0, 0];
    return {
        originX: (sheet?.startIndent ?? 0) + trimming[0],
        originY: (sheet?.startIndent ?? 0) + trimming[2],
        sheetLength: Number(sheet?.length) || 0,
        sheetWidth: Number(sheet?.width) || 0,
    };
};

/** Screen → mm from inner sheet corner; Y down from top edge. */
const screenToSheetMm = (clientX, clientY, camera, gl, totalHeight, originX, originY) => {
    const bounds = gl.domElement.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
        return null;
    }
    const ndcX = ((clientX - bounds.left) / bounds.width) * 2 - 1;
    const ndcY = -(((clientY - bounds.top) / bounds.height) * 2 - 1);
    const world = new Vector3(ndcX, ndcY, 0).unproject(camera);
    const javaY = totalHeight - world.y;
    return {
        x: world.x - originX,
        y: javaY - originY,
    };
};

/** Convert Java (Y down) → Three (Y up), given canvas height (= Java BufferedImage). */
const makeFlipY = (totalHeight) => (y) => totalHeight - y;

/**
 * Screen px for a Java stroke of `javaPx` on the BufferedImage,
 * when the full canvas is fitted into the WebGL viewport.
 * Floor at 1 so lines stay visible; cap keeps gaps from being eaten.
 */
const StrokeScaleContext = createContext(1);
const useStroke = (javaPx) => {
    const scale = useContext(StrokeScaleContext);
    // Keep at least 1px so thin cut-off / detail borders stay visible
    return Math.min(1.5, Math.max(1, javaPx * scale));
};

const noRaycast = () => null;

const RectMesh = ({
                      x,
                      y,
                      width,
                      height,
                      color,
                      flipY,
                      z = 0,
                      renderOrder = 1,
                      depthTest = true,
                      opacity = DETAIL_OPACITY,
                      onPointerOver,
                      onPointerOut,
                  }) => {
    const shape = useMemo(() => {
        const s = new Shape();
        const y0 = flipY(y);
        const y1 = flipY(y + height);
        s.moveTo(x, y0);
        s.lineTo(x + width, y0);
        s.lineTo(x + width, y1);
        s.lineTo(x, y1);
        s.closePath();
        return s;
    }, [x, y, width, height, flipY]);

    return (
        <mesh
            position={[0, 0, z]}
            renderOrder={renderOrder}
            onPointerOver={onPointerOver}
            onPointerOut={onPointerOut}
        >
            <shapeGeometry args={[shape]}/>
            <meshBasicMaterial
                color={color}
                toneMapped={false}
                transparent
                opacity={opacity}
                depthWrite={false}
                depthTest={depthTest}
            />
        </mesh>
    );
};

const MapLine = ({
                     points,
                     color,
                     lineWidth = 1,
                     opacity = 1,
                     dashed = false,
                     dashSize = 40,
                     gapSize = 20,
                     renderOrder = 2,
                     interactive = false,
                     onPointerOver,
                     onPointerOut,
                 }) => {
    const ref = useRef();
    useLayoutEffect(() => {
        if (dashed && ref.current?.computeLineDistances) {
            ref.current.computeLineDistances();
        }
    }, [points, dashed, dashSize, gapSize]);

    return (
        <Line
            ref={ref}
            points={points}
            color={color}
            lineWidth={lineWidth}
            dashed={dashed}
            dashSize={dashSize}
            gapSize={gapSize}
            toneMapped={false}
            depthTest={false}
            renderOrder={renderOrder}
            transparent={opacity < 1}
            opacity={opacity}
            raycast={interactive ? undefined : noRaycast}
            onPointerOver={interactive ? onPointerOver : undefined}
            onPointerOut={interactive ? onPointerOut : undefined}
        />
    );
};

/** Invisible hit strip along a kerf so dashed segments act as one interactive cut. */
const CutHitArea = ({from, to, flipY, onPointerOver, onPointerOut}) => {
    const x0 = from[0];
    const y0 = flipY(from[1]);
    const x1 = to[0];
    const y1 = flipY(to[1]);
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    const thickness = 24;
    const horizontal = Math.abs(y1 - y0) <= Math.abs(x1 - x0);

    return (
        <mesh
            position={[cx, cy, 0.4]}
            renderOrder={9}
            onPointerOver={onPointerOver}
            onPointerOut={onPointerOut}
        >
            <planeGeometry args={horizontal ? [len, thickness] : [thickness, len]}/>
            <meshBasicMaterial transparent opacity={0} depthWrite={false} toneMapped={false}/>
        </mesh>
    );
};

const RectOutline = ({
                        x,
                        y,
                        width,
                        height,
                        flipY,
                        color = DETAIL_OUTLINE_COLOR,
                        javaStroke = OUTLINE_LINE_WIDTH,
                        renderOrder = 3,
                    }) => {
    const y0 = flipY(y);
    const y1 = flipY(y + height);
    const lineWidth = useStroke(javaStroke);
    return (
        <MapLine
            points={[
                [x, y0, 0],
                [x + width, y0, 0],
                [x + width, y1, 0],
                [x, y1, 0],
                [x, y0, 0],
            ]}
            color={color}
            lineWidth={lineWidth}
            renderOrder={renderOrder}
        />
    );
};

const SheetOutline = ({sheet, flipY}) => {
    const {length, width, trimming, startIndent} = sheet;
    const [t0, t1, t2, t3] = trimming;
    const outerW = t0 + length + t1;
    const outerH = t2 + width + t3;
    return (
        <group>
            <RectOutline
                x={startIndent}
                y={startIndent}
                width={outerW}
                height={outerH}
                flipY={flipY}
                color="#3B8BEC"
                javaStroke={SHEET_OUTER_LINE_WIDTH}
            />
            <RectOutline
                x={startIndent + t0}
                y={startIndent + t2}
                width={length}
                height={width}
                flipY={flipY}
                color={SHEET_INNER_COLOR}
                javaStroke={OUTLINE_LINE_WIDTH}
            />
        </group>
    );
};

const HatchGroup = ({lines, color, flipY, renderOrder = 4}) => {
    const lineWidth = useStroke(HATCH_LINE_WIDTH);
    return (
        <group>
            {lines.map((segment, i) => (
                <MapLine
                    key={i}
                    points={[
                        [segment[0][0], flipY(segment[0][1]), 0],
                        [segment[1][0], flipY(segment[1][1]), 0],
                    ]}
                    color={color}
                    lineWidth={lineWidth}
                    opacity={HATCH_OPACITY}
                    renderOrder={renderOrder}
                />
            ))}
        </group>
    );
};

/** Invisible hit rect for cut-off / waste hover */
const HitRect = ({x, y, width, height, flipY, z = 0.2, onPointerOver, onPointerOut}) => {
    const y0 = flipY(y);
    const y1 = flipY(y + height);
    const cy = (y0 + y1) / 2;
    const h = Math.abs(y1 - y0);
    return (
        <mesh
            position={[x + width / 2, cy, z]}
            renderOrder={9}
            onPointerOver={onPointerOver}
            onPointerOut={onPointerOut}
        >
            <planeGeometry args={[width, h]}/>
            <meshBasicMaterial transparent opacity={0} depthWrite={false} toneMapped={false}/>
        </mesh>
    );
};

const MapLabel = ({
                      text,
                      position,
                      rotate = false,
                      fontSize,
                      renderOrder = 10,
                  }) => (
    <Text
        position={position}
        rotation={rotate ? [0, 0, Math.PI / 2] : [0, 0, 0]}
        fontSize={fontSize}
        color="#000000"
        anchorX="center"
        anchorY="middle"
        renderOrder={renderOrder}
        depthTest={false}
        raycast={noRaycast}
    >
        {text}
    </Text>
);

const CLICK_DRAG_THRESHOLD_PX = 5;

const clampCameraToBounds = (camera, size, totalWidth, totalHeight) => {
    const centerX = totalWidth / 2;
    const centerY = totalHeight / 2;
    const visibleWidth = size.width / camera.zoom;
    const visibleHeight = size.height / camera.zoom;
    const minX = visibleWidth >= totalWidth ? centerX : visibleWidth / 2;
    const maxX = visibleWidth >= totalWidth ? centerX : totalWidth - visibleWidth / 2;
    const minY = visibleHeight >= totalHeight ? centerY : visibleHeight / 2;
    const maxY = visibleHeight >= totalHeight ? centerY : totalHeight - visibleHeight / 2;
    camera.position.x = Math.min(maxX, Math.max(minX, camera.position.x));
    camera.position.y = Math.min(maxY, Math.max(minY, camera.position.y));
};

/** Map-like controls: wheel zoom, drag to pan. */
const MapCursorPan = ({enabled, zoom, onZoomChange, totalWidth, totalHeight}) => {
    const {camera, gl, size} = useThree();

    useEffect(() => {
        camera.zoom = zoom;
        camera.updateProjectionMatrix();
        clampCameraToBounds(camera, size, totalWidth, totalHeight);

        if (!enabled) {
            return undefined;
        }

        let dragging = false;
        let lastClientX = 0;
        let lastClientY = 0;

        const onPointerDown = (event) => {
            if (event.button !== 0) {
                return;
            }
            dragging = true;
            lastClientX = event.clientX;
            lastClientY = event.clientY;
            try {
                gl.domElement.setPointerCapture(event.pointerId);
            } catch (e) {
                // ignore
            }
        };

        const onPointerMove = (event) => {
            if (!dragging) {
                return;
            }
            const dx = event.clientX - lastClientX;
            const dy = event.clientY - lastClientY;
            lastClientX = event.clientX;
            lastClientY = event.clientY;
            camera.position.x -= dx / camera.zoom;
            camera.position.y += dy / camera.zoom;
            clampCameraToBounds(camera, size, totalWidth, totalHeight);
        };

        const onPointerUp = (event) => {
            if (!dragging) {
                return;
            }
            dragging = false;
            try {
                gl.domElement.releasePointerCapture(event.pointerId);
            } catch (e) {
                // ignore
            }
        };

        const onWheel = (event) => {
            event.preventDefault();
            onZoomChange((currentZoom) => {
                const step = event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
                return Math.min(MAX_ZOOM_MULTIPLIER, Math.max(MIN_ZOOM_MULTIPLIER, currentZoom * step));
            });
        };

        const element = gl.domElement;
        element.addEventListener('pointerdown', onPointerDown);
        element.addEventListener('pointermove', onPointerMove);
        element.addEventListener('pointerup', onPointerUp);
        element.addEventListener('pointercancel', onPointerUp);
        element.addEventListener('wheel', onWheel, {passive: false});
        return () => {
            element.removeEventListener('pointerdown', onPointerDown);
            element.removeEventListener('pointermove', onPointerMove);
            element.removeEventListener('pointerup', onPointerUp);
            element.removeEventListener('pointercancel', onPointerUp);
            element.removeEventListener('wheel', onWheel);
        };
    }, [camera, enabled, gl, onZoomChange, size, totalHeight, totalWidth, zoom]);

    return null;
};

const MapRulerCrosshair = ({
                               xMm,
                               yMm,
                               sheet,
                               totalWidth,
                               totalHeight,
                               flipY,
                           }) => {
    const {originX, originY} = getSheetOrigin(sheet);
    if (xMm == null || yMm == null || Number.isNaN(xMm) || Number.isNaN(yMm)) {
        return null;
    }
    const worldX = originX + xMm;
    const worldY = flipY(originY + yMm);
    const label = `${xMm.toFixed(1)} × ${yMm.toFixed(1)}`;
    const fontSize = Math.max(64, Math.min(totalWidth, totalHeight) * 0.04);
    const z = 60;

    return (
        <group renderOrder={1000}>
            <RulerSegment
                from={[0, worldY]}
                to={[totalWidth, worldY]}
                color={RULER_CROSS_COLOR}
                z={z}
                screenThickness={1.25}
                renderOrder={1100}
            />
            <RulerSegment
                from={[worldX, 0]}
                to={[worldX, totalHeight]}
                color={RULER_CROSS_COLOR}
                z={z}
                screenThickness={1.25}
                renderOrder={1100}
            />
            <Text
                position={[worldX + fontSize * 0.35, worldY + fontSize * 0.2, z + 3]}
                fontSize={fontSize}
                color={RULER_LABEL_COLOR}
                outlineWidth="10%"
                outlineBlur="2%"
                outlineColor={RULER_LABEL_OUTLINE_COLOR}
                outlineOpacity={1}
                anchorX="left"
                anchorY="bottom"
                renderOrder={1300}
                depthTest={false}
                raycast={noRaycast}
            >
                {label}
            </Text>
        </group>
    );
};

const MapRulerTracker = ({
                             enabled,
                             locked = false,
                             sheet,
                             totalHeight,
                             onCoordsChange,
                             onPinToggle,
                         }) => {
    const {camera, gl} = useThree();
    const {originX, originY} = getSheetOrigin(sheet);
    const lockedRef = useRef(locked);

    useEffect(() => {
        lockedRef.current = locked;
    }, [locked]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        let pointerDown = false;
        let startX = 0;
        let startY = 0;
        let moved = false;

        const readCoords = (event) => screenToSheetMm(
            event.clientX,
            event.clientY,
            camera,
            gl,
            totalHeight,
            originX,
            originY,
        );

        const onPointerMove = (event) => {
            if (pointerDown) {
                const dist = Math.hypot(event.clientX - startX, event.clientY - startY);
                if (dist > CLICK_DRAG_THRESHOLD_PX) {
                    moved = true;
                }
            }
            if (lockedRef.current) {
                return;
            }
            const coords = readCoords(event);
            if (coords) {
                onCoordsChange?.(coords);
            }
        };

        const onPointerDown = (event) => {
            if (event.button !== 0) {
                return;
            }
            pointerDown = true;
            moved = false;
            startX = event.clientX;
            startY = event.clientY;
        };

        const onPointerUp = (event) => {
            if (!pointerDown || event.button !== 0) {
                pointerDown = false;
                return;
            }
            pointerDown = false;
            if (moved) {
                return;
            }
            const coords = readCoords(event);
            onPinToggle?.(coords);
        };

        const element = gl.domElement;
        element.addEventListener('pointermove', onPointerMove);
        element.addEventListener('pointerdown', onPointerDown);
        element.addEventListener('pointerup', onPointerUp);
        element.addEventListener('pointercancel', onPointerUp);
        return () => {
            element.removeEventListener('pointermove', onPointerMove);
            element.removeEventListener('pointerdown', onPointerDown);
            element.removeEventListener('pointerup', onPointerUp);
            element.removeEventListener('pointercancel', onPointerUp);
        };
    }, [camera, enabled, gl, onCoordsChange, onPinToggle, originX, originY, totalHeight]);

    return null;
};

const SceneContent = ({
                          scene,
                          interactive = false,
                          onDetailHover,
                          zoomMultiplier,
                          setZoomMultiplier,
                          showRuler = false,
                          rulerCoords = null,
                          onRulerCoordsChange,
                          rulerLocked = false,
                          onRulerPinToggle,
                      }) => {
    const {totalWidth, totalHeight} = getSceneBounds(scene);
    const {size} = useThree();
    const [hoveredDetail, setHoveredDetail] = useState(null);
    const [hoveredCutOff, setHoveredCutOff] = useState(null);
    const [hoveredCut, setHoveredCut] = useState(null);
    const baseZoom = Math.min(size.width / totalWidth, size.height / totalHeight);
    const zoom = baseZoom * zoomMultiplier;
    const flipY = useMemo(() => makeFlipY(totalHeight), [totalHeight]);
    const {originX, originY} = useMemo(() => getSheetOrigin(scene.sheet), [scene.sheet]);

    useEffect(() => {
        if (!showRuler || !rulerLocked || !rulerCoords) {
            return;
        }
        const absX = originX + rulerCoords.x;
        const absY = originY + rulerCoords.y;
        const detailIndex = scene.details.findIndex((d) => (
            absX >= d.x && absX <= d.x + d.width && absY >= d.y && absY <= d.y + d.height
        ));
        if (detailIndex >= 0) {
            setHoveredDetail(detailIndex);
            setHoveredCutOff(null);
            onDetailHover?.(scene.details[detailIndex]);
            return;
        }
        const cutOffIndex = scene.cutOffs.findIndex((c) => (
            absX >= c.x && absX <= c.x + c.width && absY >= c.y && absY <= c.y + c.height
        ));
        if (cutOffIndex >= 0) {
            setHoveredCutOff(cutOffIndex);
            setHoveredDetail(null);
            onDetailHover?.({...scene.cutOffs[cutOffIndex], previewType: 'cutOff'});
            return;
        }
        setHoveredDetail(null);
        setHoveredCutOff(null);
        onDetailHover?.(null);
    }, [onDetailHover, originX, originY, rulerCoords, rulerLocked, scene.cutOffs, scene.details, showRuler]);

    // How many screen pixels correspond to 1 Java canvas pixel at current fit
    const strokeScale = useMemo(
        () => Math.min(size.width / totalWidth, size.height / totalHeight),
        [size.width, size.height, totalWidth, totalHeight],
    );
    const toScreenStroke = (javaPx) => Math.min(1.5, Math.max(1, javaPx * strokeScale));
    const edgeWidth = toScreenStroke(EDGE_LINE_WIDTH);
    const cutWidth = toScreenStroke(CUT_LINE_WIDTH);
    const renderDetail = (d, i) => {
        const isHovered = hoveredDetail === i;
        const cx = d.x + d.width / 2;
        const cy = flipY(d.y + d.height / 2);
        const label = d.label;
        const labelSize = label?.fontSize || 0;
        const showLabel = labelSize >= 2 || (isHovered && label?.text);
        return (
            <group key={`d-${i}`}>
                <RectMesh
                    x={d.x}
                    y={d.y}
                    width={d.width}
                    height={d.height}
                    color={d.color}
                    flipY={flipY}
                    z={0}
                    renderOrder={1}
                    opacity={isHovered ? 1 : DETAIL_OPACITY}
                    onPointerOver={(event) => {
                        event.stopPropagation();
                        if (rulerLocked) {
                            return;
                        }
                        setHoveredCutOff(null);
                        setHoveredDetail(i);
                        onDetailHover?.(d);
                    }}
                    onPointerOut={(event) => {
                        event.stopPropagation();
                        if (rulerLocked) {
                            return;
                        }
                        setHoveredDetail(null);
                        onDetailHover?.(null);
                    }}
                />
                <RectOutline
                    x={d.x}
                    y={d.y}
                    width={d.width}
                    height={d.height}
                    flipY={flipY}
                    color={DETAIL_OUTLINE_COLOR}
                    javaStroke={OUTLINE_LINE_WIDTH}
                    renderOrder={3}
                />
                {showLabel && (
                    <MapLabel
                        text={label.text}
                        position={[cx, cy, 1]}
                        rotate={label.rotate}
                        fontSize={labelSize}
                        renderOrder={10}
                    />
                )}
            </group>
        );
    };

    const renderCutOff = (c, i) => {
        const isHovered = hoveredCutOff === i;
        const cx = c.x + c.width / 2;
        const cy = flipY(c.y + c.height / 2);
        const label = c.label;
        const labelSize = label?.fontSize || 0;
        const showLabel = labelSize >= 2 || (isHovered && label?.text);
        const inset = CUTOFF_OUTLINE_INSET;
        const ow = c.width - inset * 2;
        const oh = c.height - inset * 2;
        return (
            <group key={`co-${i}`}>
                <HatchGroup
                    lines={c.hatchLines}
                    color={c.hatchColor}
                    flipY={flipY}
                    renderOrder={4}
                />
                {ow > 0 && oh > 0 && (
                    <RectOutline
                        x={c.x + inset}
                        y={c.y + inset}
                        width={ow}
                        height={oh}
                        flipY={flipY}
                        color={CUTOFF_OUTLINE_COLOR}
                        javaStroke={1}
                        renderOrder={8}
                    />
                )}
                {showLabel && (
                    <MapLabel
                        text={label.text}
                        position={[cx, cy, 1]}
                        rotate={label.rotate}
                        fontSize={labelSize}
                        renderOrder={10}
                    />
                )}
                <HitRect
                    x={c.x}
                    y={c.y}
                    width={c.width}
                    height={c.height}
                    flipY={flipY}
                    onPointerOver={(event) => {
                        event.stopPropagation();
                        if (rulerLocked) {
                            return;
                        }
                        setHoveredDetail(null);
                        setHoveredCutOff(i);
                        onDetailHover?.({...c, previewType: 'cutOff'});
                    }}
                    onPointerOut={(event) => {
                        event.stopPropagation();
                        if (rulerLocked) {
                            return;
                        }
                        setHoveredCutOff(null);
                        onDetailHover?.(null);
                    }}
                />
            </group>
        );
    };

    return (
        <StrokeScaleContext.Provider value={strokeScale}>
            <OrthographicCamera
                makeDefault
                position={[totalWidth / 2, totalHeight / 2, 200]}
                zoom={zoom}
                near={0.1}
                far={1000}
            />
            <MapCursorPan
                enabled={interactive}
                zoom={zoom}
                onZoomChange={setZoomMultiplier}
                totalWidth={totalWidth}
                totalHeight={totalHeight}
            />
            {showRuler && (
                <MapRulerTracker
                    enabled={showRuler}
                    locked={rulerLocked}
                    sheet={scene.sheet}
                    totalHeight={totalHeight}
                    onCoordsChange={onRulerCoordsChange}
                    onPinToggle={onRulerPinToggle}
                />
            )}
            <color attach="background" args={['#ffffff']}/>
            <group>
                <SheetOutline sheet={scene.sheet} flipY={flipY}/>

                {scene.details.map((d, i) => renderDetail(d, i))}

                {scene.cutOffs.map((c, i) => renderCutOff(c, i))}

                {scene.wastes.map((w, i) => (
                    <group key={`w-${i}`}>
                        <HatchGroup lines={w.hatchLines} color={w.hatchColor} flipY={flipY}/>
                    </group>
                ))}

                {scene.edges.map((e, i) => (
                    <MapLine
                        key={`e-${i}`}
                        points={[
                            [e.from[0], flipY(e.from[1]), 0],
                            [e.to[0], flipY(e.to[1]), 0],
                        ]}
                        color={DETAIL_OUTLINE_COLOR}
                        lineWidth={edgeWidth}
                        renderOrder={5}
                    />
                ))}

                {scene.cutClears?.map((c, i) => (
                    <MapLine
                        key={`cc-${i}`}
                        points={[
                            [c.from[0], flipY(c.from[1]), 0],
                            [c.to[0], flipY(c.to[1]), 0],
                        ]}
                        color={c.color}
                        lineWidth={cutWidth + 1}
                        renderOrder={6}
                    />
                ))}

                {scene.cuts.map((cut, i) => {
                    const cutHovered = hoveredCut === i;
                    const color = cutHovered ? HOVER_COLOR : cut.color;
                    const lineWidth = cutHovered ? cutWidth + 1.5 : cutWidth;
                    return (
                        <group key={`cut-${cut.id ?? i}`}>
                            {(cut.segments ?? [cut]).map((seg, si) => (
                                <MapLine
                                    key={`c-${i}-${si}`}
                                    points={[
                                        [seg.from[0], flipY(seg.from[1]), 0],
                                        [seg.to[0], flipY(seg.to[1]), 0],
                                    ]}
                                    color={color}
                                    lineWidth={lineWidth}
                                    renderOrder={7}
                                />
                            ))}
                            <CutHitArea
                                from={cut.from}
                                to={cut.to}
                                flipY={flipY}
                                onPointerOver={(event) => {
                                    event.stopPropagation();
                                    setHoveredCut(i);
                                }}
                                onPointerOut={(event) => {
                                    event.stopPropagation();
                                    setHoveredCut(null);
                                }}
                            />
                        </group>
                    );
                })}

                {scene.labels.map((label, i) => {
                    if (!label.fontSize || label.fontSize < 2) {
                        return null;
                    }
                    return (
                        <Text
                            key={`l-${i}`}
                            position={[label.x, flipY(label.y), 1]}
                            rotation={label.rotate ? [0, 0, Math.PI / 2] : [0, 0, 0]}
                            fontSize={label.fontSize}
                            color="#000000"
                            anchorX="center"
                            anchorY="middle"
                            renderOrder={10}
                            depthTest={false}
                            raycast={noRaycast}
                        >
                            {label.text}
                        </Text>
                    );
                })}
            </group>

            {showRuler && (
                <MapRulerCrosshair
                    xMm={rulerCoords?.x}
                    yMm={rulerCoords?.y}
                    sheet={scene.sheet}
                    totalWidth={totalWidth}
                    totalHeight={totalHeight}
                    flipY={flipY}
                />
            )}
        </StrokeScaleContext.Provider>
    );
};

/**
 * Векторная отрисовка карты раскроя по JSON CuttingMap (как DrawMapUtil).
 */
const CuttingMapCanvas = ({
                              cuttingMap,
                              drawEdge = true,
                              drawPosition = true,
                              onDetailHover,
                              showRuler = false,
                              rulerCoords = null,
                              onRulerCoordsChange,
                              rulerLocked = false,
                              onRulerPinToggle,
                          }) => {
    const [interactive, setInteractive] = useState(false);
    const [zoomMultiplier, setZoomMultiplier] = useState(MIN_ZOOM_MULTIPLIER);
    const scene = useMemo(
        () => buildCuttingMapScene(cuttingMap, {drawEdge, drawPosition}),
        [cuttingMap, drawEdge, drawPosition],
    );
    if (!cuttingMap) {
        return null;
    }

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
            }}
            onMouseEnter={() => setInteractive(true)}
            onMouseLeave={() => setInteractive(false)}
            onContextMenu={(event) => {
                event.preventDefault();
                setZoomMultiplier(MIN_ZOOM_MULTIPLIER);
            }}
        >
            <Canvas
                orthographic
                style={{width: '100%', height: '100%'}}
                dpr={[1, 2]}
                gl={{antialias: true}}
            >
                <SceneContent
                    scene={scene}
                    interactive={interactive}
                    onDetailHover={onDetailHover}
                    zoomMultiplier={zoomMultiplier}
                    setZoomMultiplier={setZoomMultiplier}
                    showRuler={showRuler}
                    rulerCoords={rulerCoords}
                    onRulerCoordsChange={onRulerCoordsChange}
                    rulerLocked={rulerLocked}
                    onRulerPinToggle={onRulerPinToggle}
                />
            </Canvas>
        </div>
    );
};

export default CuttingMapCanvas;
